import { useEffect, useRef, useState } from 'react'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Modal, ModalContent, ModalFooterWithActions, ModalFooterWithActionsType } from '@/components/modal'
import { FilePdf } from '@/assets/icons'
import { ApiError } from '@/util/errors/api-error'

/** Copy shown when the removal request itself fails for a reason we don't recognise. */
const kGenericRemoveErrorMessage = 'Could not remove the file. Please try again.'

type MatterRecordRemoveFileDialogProps<TImpact> = {
  open: boolean
  /** File name shown on the file row. */
  displayName: string
  onClose: () => void
  /** Modal heading, e.g. "Remove file from chronology". */
  title: string
  /** Destructive confirm label, e.g. "Remove and regenerate". */
  confirmLabel: string
  /**
   * Identifies the file whose impact is shown, e.g. `${assetId}:${userFileId}`. The dialog refetches
   * when this changes, so `fetchImpact` doesn't have to be referentially stable.
   */
  impactKey: string
  /** Loads the removal impact when the dialog opens. */
  fetchImpact: () => Promise<TImpact>
  /** Turns the loaded impact into the dialog's explanatory sentence. */
  describeImpact: (impact: TImpact) => string
  /** Sentence shown instead when the impact fetch fails. */
  impactUnavailableMessage: string
  /** Copy for a 409 — the asset is mid-update and can't accept a removal right now. */
  conflictMessage: string
  /** Performs the removal (delete, analytics, store refresh, toast). Rejecting surfaces an error. */
  onConfirm: () => Promise<void>
}

/**
 * Remove-file confirmation: fetches the removal impact on open, then synchronously removes the file
 * and regenerates the asset from the remaining files.
 *
 * Presentational plus the shared load/remove state machine: the feature supplies the copy, the
 * impact fetch and its sentence, and the removal itself. A 409 from `onConfirm` is reported with the
 * feature's `conflictMessage`; any other rejection falls back to generic copy.
 */
export default function MatterRecordRemoveFileDialog<TImpact>(props: MatterRecordRemoveFileDialogProps<TImpact>) {
  const { open, displayName, onClose, title, confirmLabel, impactKey, fetchImpact, describeImpact, impactUnavailableMessage, conflictMessage, onConfirm } =
    props

  const [impact, setImpact] = useState<TImpact | null>(null)
  const [impactError, setImpactError] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  // Read through a ref so an inline `fetchImpact` prop doesn't retrigger the fetch every render.
  const latestFetchImpact = useRef(fetchImpact)
  useEffect(() => {
    latestFetchImpact.current = fetchImpact
  })

  useEffect(() => {
    if (!open) return
    setImpact(null)
    setImpactError(false)
    setRemoveError(null)

    latestFetchImpact
      .current()
      .then(setImpact)
      .catch((error) => {
        console.error('Error fetching removal impact:', error)
        setImpactError(true)
      })
  }, [open, impactKey])

  const handleRemove = async () => {
    if (removing) return
    setRemoving(true)
    setRemoveError(null)
    try {
      await onConfirm()
      onClose()
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setRemoveError(conflictMessage)
      } else {
        setRemoveError(kGenericRemoveErrorMessage)
      }
    } finally {
      setRemoving(false)
    }
  }

  if (!open) return null

  const impactSentence = impactError ? impactUnavailableMessage : impact === null ? null : describeImpact(impact)

  const footer = (
    <div className="[&_button]:min-w-20">
      <ModalFooterWithActions
        type={ModalFooterWithActionsType.DESTRUCTIVE}
        secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: removing }}
        primaryAction={{ label: confirmLabel, onClick: handleRemove, disabled: removing || impactSentence === null, loading: removing }}
      />
    </div>
  )

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !removing) onClose()
      }}
    >
      <ModalContent title={title} width="md" height="max-md" className="max-w-[568px]" bodyClassName="-mt-1" footer={footer}>
        <div className="flex flex-col gap-2">
          {impactSentence === null ? (
            <div className="flex items-center gap-2 text-sm text-brand-neutral-600">
              <CircularProgressContinuousSized size={14} thickness={4} />
              Calculating impact…
            </div>
          ) : (
            <p className="text-base leading-6 tracking-[-0.32px] text-brand-neutral-950">{impactSentence}</p>
          )}

          <div className="flex h-12 w-full shrink-0 items-center gap-2.5 overflow-hidden py-3">
            <FilePdf className="w-6 h-6 text-brand-neutral-950 shrink-0" />
            <span className="text-sm font-medium tracking-[-0.15px] text-brand-neutral-950 truncate" title={displayName}>
              {displayName}
            </span>
          </div>

          {removeError && <p className="text-sm text-red-700">{removeError}</p>}
        </div>
      </ModalContent>
    </Modal>
  )
}
